import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Check, Flame, AlertCircle, BookOpen, Plus, X, Search, Trash2, Sparkles,
} from "lucide-react";
import { ICON_MAP, GOAL_ICONS } from "@/components/icons/IslamicIcons";
import { cn } from "@/lib/utils";

interface Quote {
  text_tr: string;
  text_ar?: string;
  source: string;
  source_detail?: string;
}

interface Goal {
  id: string;
  title: string;
  emoji: string;
  points: number;
  mandatory: boolean;
  category: string;
  sadakaCost: number;
  completed: boolean;
}

interface HomeScreenProps {
  userName: string;
  streak: number;
  sadakaDebt: number;
  totalPoints: number;
  goals: Goal[];
  quote?: Quote | null;
  onToggleGoal: (goalId: string, completed: boolean) => void;
  onOpenQuiz: () => void;
  onAddGoal?: (goal: { title: string; emoji: string; points: number; category: string }) => void;
  onDeleteGoal?: (goalId: string) => void;
}

function getGoalColor(completed: boolean, hour: number): string {
  if (completed) return "border-ehille-green bg-ehille-green/10";
  if (hour < 12) return "border-gold bg-gold/10";
  if (hour < 17) return "border-ehille-orange bg-ehille-orange/10";
  return "border-ehille-red bg-ehille-red/10";
}

function getGoalCheckColor(completed: boolean, hour: number): string {
  if (completed) return "bg-ehille-green text-white";
  if (hour < 12) return "bg-gold/20 text-gold";
  if (hour < 17) return "bg-ehille-orange/20 text-ehille-orange";
  return "bg-ehille-red/20 text-ehille-red";
}

export function HomeScreen({
  userName, streak, sadakaDebt, totalPoints, goals, quote, onToggleGoal, onOpenQuiz, onAddGoal, onDeleteGoal,
}: HomeScreenProps) {
  // Turkey timezone (UTC+3) for greeting
  const getTurkeyHour = () => {
    const now = new Date();
    const turkeyTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Istanbul" }));
    return turkeyTime.getHours();
  };
  const [hour, setHour] = useState(getTurkeyHour());
  const [filter, setFilter] = useState<"all" | "mandatory" | "personal">("all");
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: "", emoji: "book", points: 10, category: "kisisel" });

  useEffect(() => {
    const interval = setInterval(() => setHour(getTurkeyHour()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const completedCount = goals.filter(g => g.completed).length;
  const totalGoals = goals.length;
  const progressPct = totalGoals > 0 ? Math.round((completedCount / totalGoals) * 100) : 0;
  const mandatoryMissed = goals.filter(g => g.mandatory && !g.completed).length;

  const filteredGoals = goals.filter(g => {
    if (filter === "mandatory") return g.mandatory;
    if (filter === "personal") return !g.mandatory;
    return true;
  });

  const greeting = hour >= 5 && hour < 12 ? "Gunaydin"
    : hour >= 12 && hour < 17 ? "Iyi gunler"
    : hour >= 17 && hour < 22 ? "Iyi aksamlar"
    : "Gecen hayr olsun";

  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-muted-foreground text-sm">{greeting},</p>
          <h1 className="text-xl font-bold">{userName}</h1>
        </div>
        <div className="flex items-center gap-2">
          {streak > 0 && (
            <div className="flex items-center gap-1.5 bg-ehille-orange/10 rounded-full px-3 py-1.5" title="Günlük seri">
              <Flame size={16} className="text-ehille-orange animate-flicker" />
              <span className="text-sm font-bold text-ehille-orange">{streak}</span>
            </div>
          )}
          <div className="bg-gold/10 rounded-full px-3 py-1.5 text-center" title="Toplam puanınız">
            <p className="text-[9px] text-gold/60 leading-none uppercase tracking-wider">Toplam</p>
            <p className="text-sm font-bold text-gold leading-tight">{totalPoints}</p>
          </div>
        </div>
      </div>

      {/* Daily motivational quote — with label */}
      {quote && (
        <Card className="mb-4 border-gold/20 bg-gradient-to-br from-ehille-dark to-ehille-dark2 overflow-hidden relative">
          <div className="absolute top-2 right-3 text-4xl opacity-10 select-none">🌙</div>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-gold" />
              <p className="text-[11px] font-bold text-gold uppercase tracking-wider">
                {quote.source === "Hadis" ? "Günün Hadisi" : quote.source === "Kuran" ? "Günün Ayeti" : "Günün Özlü Sözü"}
              </p>
            </div>
            {quote.text_ar && (
              <p className="text-gold/80 text-sm text-right leading-relaxed mb-2 font-serif" dir="rtl">
                {quote.text_ar}
              </p>
            )}
            <p className="text-white text-sm leading-relaxed italic">
              "{quote.text_tr}"
            </p>
            <p className="text-gold/60 text-[11px] mt-2 font-medium">
              — {quote.source}{quote.source_detail ? `, ${quote.source_detail}` : ""}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sadaka warning */}
      {sadakaDebt > 0 && (
        <Card className="mb-4 border-ehille-red/30 bg-ehille-red/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle size={20} className="text-ehille-red shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-ehille-red">Sadaka Borcu</p>
              <p className="text-xs text-muted-foreground">Geçmiş hedeflerden {sadakaDebt.toFixed(0)} TL borcunuz var</p>
            </div>
            <Badge variant="destructive" className="shrink-0">{sadakaDebt.toFixed(0)} TL</Badge>
          </CardContent>
        </Card>
      )}

      {/* Daily progress */}
      <Card className="mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm">Günlük İlerleme</h3>
            <span className="text-sm font-bold text-gold">{progressPct}%</span>
          </div>
          <Progress value={progressPct} className="h-3" />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted-foreground">
              {completedCount}/{totalGoals} hedef tamamlandı
            </p>
            {mandatoryMissed > 0 && (
              <p className="text-xs text-ehille-red font-medium">
                {mandatoryMissed} zorunlu kaldı
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quiz CTA */}
      <Card
        className="mb-6 border-gold/30 bg-gradient-to-r from-gold/5 to-gold/10 cursor-pointer hover:border-gold/50 transition-all active:scale-[0.98]"
        onClick={onOpenQuiz}
      >
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center shrink-0">
            <BookOpen size={20} className="text-gold" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm">Haftalık Sınav</p>
            <p className="text-xs text-muted-foreground">Bilginizi test edin, puan kazanın</p>
          </div>
          <Badge variant="warning">Yeni</Badge>
        </CardContent>
      </Card>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {(["all", "mandatory", "personal"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
              filter === f
                ? "bg-gold text-ehille-dark"
                : "bg-muted text-muted-foreground hover:text-foreground",
            )}
          >
            {f === "all" ? "Tümü" : f === "mandatory" ? "Zorunlu" : "Kişisel"}
          </button>
        ))}
      </div>

      {/* Goals list */}
      <div className="space-y-3">
        {filteredGoals.map((goal) => (
          <Card
            key={goal.id}
            className={cn(
              "transition-all duration-300 border-2 cursor-pointer active:scale-[0.98] group relative",
              getGoalColor(goal.completed, hour),
            )}
            onClick={() => onToggleGoal(goal.id, !goal.completed)}
          >
            <CardContent className="p-4 flex items-center gap-4">
              {/* Check circle */}
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all",
                  getGoalCheckColor(goal.completed, hour),
                )}
              >
                {goal.completed ? (
                  <Check size={18} strokeWidth={3} />
                ) : (
                  ICON_MAP[goal.emoji]
                    ? (() => { const I = ICON_MAP[goal.emoji]; return <I size={20} />; })()
                    : <span className="text-lg">{goal.emoji}</span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={cn(
                    "font-semibold text-sm truncate transition-colors",
                    goal.completed && "text-ehille-green",
                  )}>
                    {goal.title}
                  </h4>
                  {goal.mandatory && (
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 shrink-0">
                      Zorunlu
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground capitalize">{goal.category}</span>
                  {goal.sadakaCost > 0 && (
                    <span className="text-[10px] text-ehille-red/70">
                      Ceza: {goal.sadakaCost} ₺
                    </span>
                  )}
                </div>
              </div>

              {/* Points — shows "+X" when earned, "X puan" otherwise */}
              <div className="text-right shrink-0 flex items-center gap-2">
                {goal.completed ? (
                  <div className="flex items-center gap-1 bg-ehille-green/15 rounded-full px-2.5 py-1">
                    <Check size={12} className="text-ehille-green" strokeWidth={3} />
                    <span className="text-xs font-bold text-ehille-green">+{goal.points}</span>
                  </div>
                ) : (
                  <div className="text-right">
                    <span className="text-sm font-bold text-gold">+{goal.points}</span>
                    <p className="text-[9px] text-muted-foreground leading-none">puan</p>
                  </div>
                )}
                {/* Delete button — only for personal (non-mandatory) goals */}
                {!goal.mandatory && onDeleteGoal && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`"${goal.title}" hedefini silmek istediğine emin misin?`)) {
                        onDeleteGoal(goal.id);
                      }
                    }}
                    className="w-7 h-7 rounded-full bg-muted/50 hover:bg-ehille-red/20 text-muted-foreground hover:text-ehille-red flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                    title="Hedefi sil"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Add personal goal button */}
        {onAddGoal && (
          <button
            onClick={() => setShowAddGoal(true)}
            className="w-full p-4 rounded-2xl border-2 border-dashed border-border hover:border-gold/50 flex items-center justify-center gap-2 text-muted-foreground hover:text-gold transition-all"
          >
            <Plus size={18} />
            <span className="text-sm font-semibold">Kişisel Hedef Ekle</span>
          </button>
        )}
      </div>

      {/* Add Goal Modal */}
      {showAddGoal && onAddGoal && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowAddGoal(false)}>
          <div className="bg-card w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Sticky header */}
            <div className="sticky top-0 bg-card border-b border-border p-5 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-bold">Kişisel Hedef Ekle</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">Kendi hedefini belirle ve takip et</p>
              </div>
              <button onClick={() => setShowAddGoal(false)} className="w-9 h-9 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Title — primary field first */}
              <div>
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block mb-2">
                  Hedef Adı <span className="text-ehille-red">*</span>
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={newGoal.title}
                    onChange={e => setNewGoal(g => ({ ...g, title: e.target.value }))}
                    placeholder="Örn: Sabah Zikri, 30 dk spor, Kuran okumak..."
                    autoFocus
                    className="w-full h-12 rounded-xl border border-input bg-background pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
                {newGoal.title.length === 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-2.5">
                    <p className="text-[10px] text-muted-foreground w-full mb-1">Hızlı Öneriler:</p>
                    {["Sabah Zikri", "Akşam Zikri", "Kuran Dinle", "Podcast Dinle", "YouTube Ders", "Siyer Oku", "Hadis Oku", "Meal Oku", "Tefekkür", "Spor", "Su İç"].map(s => (
                      <button
                        key={s}
                        onClick={() => setNewGoal(g => ({ ...g, title: s }))}
                        className="px-2.5 py-1 rounded-full bg-muted text-[10px] font-medium text-muted-foreground hover:text-gold hover:bg-gold/10 transition-all"
                      >{s}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Icon picker */}
              <div>
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block mb-2">İkon Seç</label>
                <div className="grid grid-cols-6 gap-2">
                  {GOAL_ICONS.map(icon => {
                    const IconComp = ICON_MAP[icon.id];
                    return (
                      <button
                        key={icon.id}
                        onClick={() => setNewGoal(g => ({ ...g, emoji: icon.id }))}
                        title={icon.label}
                        className={cn(
                          "aspect-square rounded-xl flex flex-col items-center justify-center transition-all gap-0.5",
                          newGoal.emoji === icon.id
                            ? "bg-gold/20 ring-2 ring-gold text-gold scale-105"
                            : "bg-muted text-muted-foreground hover:bg-muted/70 hover:scale-105",
                        )}
                      >
                        {IconComp && <IconComp size={20} />}
                        <span className="text-[8px] font-medium leading-none">{icon.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Points */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Puan Değeri</label>
                  <span className="text-sm font-bold text-gold">{newGoal.points} puan</span>
                </div>
                <input
                  type="range" min="5" max="50" step="5"
                  value={newGoal.points}
                  onChange={e => setNewGoal(g => ({ ...g, points: parseInt(e.target.value) }))}
                  className="w-full accent-gold"
                />
                <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                  <span>5 (Kolay)</span>
                  <span>25 (Orta)</span>
                  <span>50 (Zor)</span>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block mb-2">Kategori</label>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { id: "ibadet", label: "İbadet" }, { id: "saglik", label: "Sağlık" },
                    { id: "egitim", label: "Eğitim" }, { id: "kisisel", label: "Kişisel" },
                    { id: "sosyal", label: "Sosyal" },
                  ].map(c => (
                    <button
                      key={c.id}
                      onClick={() => setNewGoal(g => ({ ...g, category: c.id }))}
                      className={cn(
                        "py-2 rounded-lg text-xs font-semibold transition-all",
                        newGoal.category === c.id ? "bg-gold text-ehille-dark" : "bg-muted text-muted-foreground hover:bg-muted/70",
                      )}
                    >{c.label}</button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => setShowAddGoal(false)}
                >
                  İptal
                </Button>
                <Button
                  size="lg"
                  className="flex-1 gap-2"
                  disabled={!newGoal.title.trim()}
                  onClick={() => {
                    onAddGoal(newGoal);
                    setShowAddGoal(false);
                    setNewGoal({ title: "", emoji: "book", points: 10, category: "kisisel" });
                  }}
                >
                  <Plus size={16} />
                  Hedef Ekle
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
